import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { sharedStorage } from 'src/common/storage/storage';

import {
  WaOrganizationSalesOfficeLocation,
  WaStatusOptionsGroup,
} from '../repository/WaOptionsRepo';
import { WaWorkplaceFilterParameters } from './models/workplace/WaWorkplaceFilterParameter';
import { isEqual } from 'lodash';
import { OperationStatus } from 'src/api/wa/store/models/workplace/OperationStatus';

export interface WaBranchOption {
  code: string;
  name: string;
}

export interface WaSalesOfficeOption extends WaBranchOption {
  aliasBranchCode: string;
  aliasSalesOfficeName: string;
  location?: WaOrganizationSalesOfficeLocation;
  defaultScale?: number;
  searchRange?: number;
}

export interface WaSectionOption extends WaBranchOption {
  location?: WaOrganizationSalesOfficeLocation;
  defaultScale?: number;
  searchRange?: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface WaSquadOption extends WaBranchOption {}

type MapLocation = {
  latitude: number;
  longitude: number;
};

type WaWorkplaceFilterParametersPatch = {
  [K in keyof WaWorkplaceFilterParameters]?: WaWorkplaceFilterParameters[K] | undefined;
};

export interface FilterStore {
  branchOptions: WaBranchOption[];
  salesOfficeOptions: WaSalesOfficeOption[];
  sectionOptions: WaSectionOption[];
  squadOptions: WaSquadOption[];
  reserved: boolean | undefined;
  statusOptions: WaStatusOptionsGroup[];
  workplaceFilterParameters: WaWorkplaceFilterParameters;
  mapCenter: MapLocation | null;
  mapCenterUserLocation: boolean;
  userOfficeLocation: { latitude: number; longitude: number } | null;
  filterChanged: boolean;
  customerBranchCode: string;
  filtersInitialized: boolean;
  setBranchOptions: (options: WaBranchOption[]) => void;
  setSalesOfficeOptions: (options: WaSalesOfficeOption[]) => void;
  setSectionOptions: (options: WaSectionOption[]) => void;
  setSquadOptions: (options: WaSquadOption[]) => void;
  setReserved: (reserved: boolean | undefined) => void;
  setStatusOptions: (options: WaStatusOptionsGroup[]) => void;
  patchWorkplaceFilterParameters: (patch: WaWorkplaceFilterParametersPatch) => void;
  setMapCenter: (location: MapLocation | null) => void;
  setMapCenterUserLocation: (mapCenterUserLocation: boolean) => void;
  setUserOfficeLocation: (location: { latitude: number; longitude: number } | null) => void;
  setFilterChanged: (changed: boolean) => void;
  setCustomerBranchCode: (code: string) => void;
  setFiltersInitialized: (initialized: boolean) => void;
  resetFilters: () => void;
  areMandatorySearchParametersSet: () => boolean;
}

const FILTER_STORE_STORAGE_KEY = 'wa-filter-store';

export const createDefaultWorkplaceFilterParameters = (): WaWorkplaceFilterParameters => ({
  branchCode: '',
  salesOfficeCode: '',
  sectionCode: '',
  squadCode: '',
  statusList: OperationStatus.Incomplete,
  customerId: '',
  reserved: false,
});

const createDefaultFilterData = () => ({
  branchOptions: [] as WaBranchOption[],
  salesOfficeOptions: [] as WaSalesOfficeOption[],
  sectionOptions: [] as WaSectionOption[],
  squadOptions: [] as WaSquadOption[],
  reserved: false as boolean | undefined,
  statusOptions: [] as WaStatusOptionsGroup[],
  workplaceFilterParameters: createDefaultWorkplaceFilterParameters(),
  mapCenter: null,
  mapCenterUserLocation: true,
  userOfficeLocation: null,
  filterChanged: false,
  customerBranchCode: '',
  filtersInitialized: false,
});

const hasMandatorySearchParameters = (
  params: WaWorkplaceFilterParameters,
  customerBranchCode: string
) => {
  // To handle Okinichi, need to allow '  ' as branch code
  const hasLocationSearch =
    Boolean(params.branchCode) &&
    Boolean(params.branchCode.length > 0) &&
    Boolean(params.salesOfficeCode?.trim());
  const hasCustomerSearch =
    Boolean(params.customerId?.trim()) && Boolean(customerBranchCode?.trim());
  return hasLocationSearch || hasCustomerSearch;
};

export const useWaFilterStore = create<FilterStore>()(
  persist(
    (set, get) => ({
      ...createDefaultFilterData(),
      setBranchOptions: (options) => set({ branchOptions: options }),
      setSalesOfficeOptions: (options) => set({ salesOfficeOptions: options }),
      setSectionOptions: (options) => set({ sectionOptions: options }),
      setSquadOptions: (options) => set({ squadOptions: options }),
      setReserved: (changed: boolean | undefined) =>
        set((state) => ({
          filterChanged: changed ?? state.filterChanged,
        })),
      setStatusOptions: (options) => set({ statusOptions: options }),
      patchWorkplaceFilterParameters: (patch: WaWorkplaceFilterParametersPatch) =>
        set((state) => {
          const newParams: WaWorkplaceFilterParameters = {
            ...state.workplaceFilterParameters,
            ...patch,
          } as WaWorkplaceFilterParameters;
          const isChanged = !isEqual(state.workplaceFilterParameters, newParams);
          return {
            workplaceFilterParameters: newParams,
            ...(isChanged && { filterChanged: true }),
          };
        }),
      setMapCenter: (location) =>
        set({
          mapCenter: location
            ? { latitude: location.latitude, longitude: location.longitude }
            : null,
        }),
      setMapCenterUserLocation: (mapCenterUserLocation: boolean) => set({ mapCenterUserLocation }),
      setUserOfficeLocation: (userOfficeLocation) => set({ userOfficeLocation }),
      setFilterChanged: (changed: boolean) => set({ filterChanged: changed }),
      setCustomerBranchCode: (code: string) => set({ customerBranchCode: code }),
      setFiltersInitialized: (initialized: boolean) => set({ filtersInitialized: initialized }),
      resetFilters: () =>
        set(() => ({
          ...createDefaultFilterData(),
        })),
      areMandatorySearchParametersSet: () => {
        const state = get();
        return hasMandatorySearchParameters(
          state.workplaceFilterParameters,
          state.customerBranchCode
        );
      },
    }),
    {
      name: FILTER_STORE_STORAGE_KEY,
      storage: createJSONStorage(() => sharedStorage),
      partialize: (state) => ({
        workplaceFilterParameters: state.workplaceFilterParameters,
        customerBranchCode: state.customerBranchCode,
        filtersInitialized: state.filtersInitialized,
      }),
    }
  )
);
